import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  v2Api,
  type ScenarioInject,
  type ScenarioInjectPayload,
  type InjectType,
  type InjectRole,
  type Phase,
  type NewPhaseInline,
  type NewTeamInline,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Pencil, Trash2, Loader2, X } from 'lucide-react';

const textareaClass =
  'flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

const SEVERITY_OPTIONS = ['low', 'medium', 'high', 'critical'];

const POINTS_TYPE_OPTIONS: { value: 'default' | 'manual' | 'decay'; label: string }[] = [
  { value: 'default', label: 'Standard' },
  { value: 'manual', label: 'Manual' },
  { value: 'decay', label: 'Decay' },
];

const TRUE_FALSE_OPTIONS = [
  { text: 'True', order: 0, is_correct: false },
  { text: 'False', order: 1, is_correct: false },
];

/** Parse API error response: standard { detail, errors } or legacy flat keys. */
function formatApiError(data: unknown, fallbackErr: unknown): string {
  if (!data || typeof data !== 'object') {
    return fallbackErr instanceof Error ? fallbackErr.message : 'Failed to save inject.';
  }
  const d = data as Record<string, unknown>;
  const parts: string[] = [];

  if (typeof d.detail === 'string') {
    parts.push(d.detail);
  }

  if (d.errors && typeof d.errors === 'object' && !Array.isArray(d.errors)) {
    const errors = d.errors as Record<string, unknown>;
    for (const [field, messages] of Object.entries(errors)) {
      if (Array.isArray(messages)) {
        const msgs = (messages as string[]).join(', ');
        parts.push(msgs ? `${field}: ${msgs}` : field);
      }
    }
  }

  if (parts.length > 0) {
    return parts.join(' — ');
  }

  for (const key of Object.keys(d)) {
    if (key === 'detail' || key === 'errors') continue;
    const v = d[key];
    if (Array.isArray(v)) {
      parts.push((v as string[]).join(', '));
    } else if (typeof v === 'string') {
      parts.push(v);
    }
  }
  if (parts.length > 0) return parts.join(' — ');
  return fallbackErr instanceof Error ? fallbackErr.message : 'Failed to save inject.';
}

interface Props {
  scenarioId: string;
}

const emptyPayload: ScenarioInjectPayload = {
  title: '',
  description: '',
  order: 1,
  severity: 'medium',
  scheduled_offset_minutes: 0,
  points: 1,
  points_type: 'default',
  max_answer_attempts: 3,
  inject_type: 0,
  options: [],
  correct_answer: '',
  detail_alert_image_url: '',
  detail_alert_text: '',
  phase_ids: [],
  team_ids: [],
  apply_to_all_team: false,
};

type ScenarioListItem = {
  id: string;
  name: string;
  ttx_enabled?: boolean;
};

export function ManageInjectsContent({ scenarioId }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const addMode = searchParams.get('add') === '1';
  const editIdParam = searchParams.get('edit');
  const editingId = editIdParam ? parseInt(editIdParam, 10) : null;
  const showForm = addMode || (editingId != null && !Number.isNaN(editingId));

  const [injects, setInjects] = useState<ScenarioInject[]>([]);
  const [injectTypes, setInjectTypes] = useState<InjectType[]>([]);
  const [phases, setPhases] = useState<Phase[]>([]);
  const [teams, setTeams] = useState<InjectRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<ScenarioInjectPayload>(emptyPayload);
  const [newPhases, setNewPhases] = useState<NewPhaseInline[]>([]);
  const [newTeams, setNewTeams] = useState<NewTeamInline[]>([]);
  const [newPhaseName, setNewPhaseName] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [phaseError, setPhaseError] = useState<string | null>(null);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [formStep, setFormStep] = useState(0);
  const [allScenarios, setAllScenarios] = useState<ScenarioListItem[]>([]);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [scenariosError, setScenariosError] = useState<string | null>(null);
  const [scenarioSearch, setScenarioSearch] = useState('');
  const [selectedScenarioIds, setSelectedScenarioIds] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteInjectId, setDeleteInjectId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const clearFormParams = useCallback(() => {
    setSearchParams({ scenario: scenarioId });
  }, [scenarioId, setSearchParams]);

  const fetchInjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, types, phasesList, rolesList] = await Promise.all([
        v2Api.dashboard.scenarios.injects.list(scenarioId),
        v2Api.dashboard.injectTypes.list(),
        v2Api.dashboard.phases.list(),
        v2Api.dashboard.roles.list(),
      ]);
      setInjects(list);
      setInjectTypes(types);
      setPhases(phasesList);
      setTeams(rolesList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load injects');
      setInjects([]);
    } finally {
      setLoading(false);
    }
  }, [scenarioId]);

  useEffect(() => {
    fetchInjects();
  }, [fetchInjects]);

  // Load all corporate scenarios once for Exercises tab mapping
  useEffect(() => {
    (async () => {
      setScenariosLoading(true);
      setScenariosError(null);
      try {
        const data = await v2Api.dashboard.scenarios.list<ScenarioListItem>();
        // v2Api.dashboard.scenarios.list returns a paginated wrapper
        const items =
          (data as unknown as { results?: ScenarioListItem[] }).results ?? [];
        // Keep only scenarios that have TTX enabled (ttx_enabled === true)
        setAllScenarios(items.filter((s) => s.ttx_enabled));
      } catch (err) {
        setAllScenarios([]);
        setScenariosError(
          err instanceof Error ? err.message : 'Failed to load scenarios for mapping',
        );
      } finally {
        setScenariosLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!showForm) return;
    setFormStep(0);
    setSaveError(null);
    setNewPhases([]);
    setNewTeams([]);
    setNewPhaseName('');
    setNewTeamName('');
    setPhaseError(null);
    setTeamError(null);
    // Reset selected scenarios on form open
    setSelectedScenarioIds([]);
    if (addMode) {
      const defaultTypeId = injectTypes[0]?.id ?? 0;
      const defaultType = injectTypes.find((t) => t.id === defaultTypeId);
      setForm({
        ...emptyPayload,
        order: injects.length > 0 ? Math.max(...injects.map((i) => i.order)) + 1 : 1,
        inject_type: defaultTypeId,
        options:
          defaultType?.code === 'true_false' ? TRUE_FALSE_OPTIONS.map((o) => ({ ...o })) : [],
        apply_to_all_team: false,
      });
      // By default, link to the current scenario
      setSelectedScenarioIds([scenarioId]);
    } else if (editingId != null) {
      const existing = injects.find((i) => i.id === editingId);
      const applyInject = (inject: ScenarioInject) => {
        setForm({
          title: inject.title,
          description: inject.description,
          order: inject.order,
          severity: inject.severity,
          scheduled_offset_minutes: inject.scheduled_offset_minutes ?? 0,
          points: inject.points ?? 0,
          points_type: inject.points_type ?? 'default',
          max_answer_attempts: inject.max_answer_attempts ?? 3,
          inject_type: inject.inject_type?.id ?? 0,
          options:
            inject.options?.map((o) => ({
              text: o.text,
              order: o.order ?? 0,
              is_correct: o.is_correct ?? false,
            })) ?? [],
          correct_answer: inject.correct_answer ?? '',
          detail_alert_image_url: inject.detail_alert_image_url ?? '',
          detail_alert_text: inject.detail_alert_text ?? '',
          phase_ids: inject.phase_ids ?? [],
          team_ids: inject.team_ids ?? [],
          apply_to_all_team: inject.apply_to_all_team ?? false,
        });
        const linked = (inject.ttx_config_ids ?? []).filter(Boolean);
        const baseIds =
          linked.length > 0
            ? linked
            : inject.scenario_id
              ? [inject.scenario_id]
              : [scenarioId];
        setSelectedScenarioIds(Array.from(new Set(baseIds.filter(Boolean))));
      };

      if (existing) {
        applyInject(existing);
      } else {
        // Fallback: fetch inject by ID (for cases where it's not yet linked to this scenario)
        (async () => {
          try {
            const inject = await v2Api.dashboard.scenarios.injects.get(scenarioId, editingId);
            if (inject) applyInject(inject as ScenarioInject);
          } catch {
            // Ignore; form will stay at defaults if fetch fails
          }
        })();
      }
    }
  }, [showForm, addMode, editingId, injects, injectTypes, scenarioId]);

  const selectedType = injectTypes.find((t) => t.id === form.inject_type);
  const hasOptions = selectedType?.has_options ?? false;
  const isTrueFalse = selectedType?.code === 'true_false';
  const isShortAnswer = selectedType?.code === 'short_answer';
  const isFillBlank = selectedType?.code === 'fill_blank';
  const isMultipleSelect = selectedType?.code === 'multiple_select';

  const openCreate = () => {
    setSearchParams({ scenario: scenarioId, add: '1' });
  };

  const openEdit = (inject: ScenarioInject) => {
    setForm({
      title: inject.title,
      description: inject.description,
      order: inject.order,
      severity: inject.severity,
      scheduled_offset_minutes: inject.scheduled_offset_minutes ?? 0,
      points: inject.points ?? 0,
      points_type: inject.points_type ?? 'default',
      max_answer_attempts: inject.max_answer_attempts ?? 3,
      inject_type: inject.inject_type?.id ?? 0,
      options:
        inject.options?.map((o) => ({
          text: o.text,
          order: o.order ?? 0,
          is_correct: o.is_correct ?? false,
        })) ?? [],
      correct_answer: inject.correct_answer ?? '',
      detail_alert_image_url: inject.detail_alert_image_url ?? '',
      detail_alert_text: inject.detail_alert_text ?? '',
      phase_ids: inject.phase_ids ?? [],
      team_ids: inject.team_ids ?? [],
      apply_to_all_team: inject.apply_to_all_team ?? false,
    });
    const linked = (inject.ttx_config_ids ?? []).filter(Boolean);
    const baseIds =
      linked.length > 0
        ? linked
        : inject.scenario_id
          ? [inject.scenario_id]
          : [scenarioId];
    setSelectedScenarioIds(Array.from(new Set(baseIds.filter(Boolean))));
    setSearchParams({ scenario: scenarioId, edit: String(inject.id) });
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setSaveError('Title is required.');
      return;
    }
    if (form.points == null || Number.isNaN(form.points) || form.points < 1) {
      setSaveError('Points is required and must be greater than 0.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload: ScenarioInjectPayload = {
        ...form,
        inject_type: form.inject_type || injectTypes[0]?.id,
        options: hasOptions ? form.options : undefined,
        correct_answer: form.correct_answer || undefined,
        detail_alert_image_url: form.detail_alert_image_url || undefined,
        detail_alert_text: form.detail_alert_text || undefined,
        phase_ids: form.phase_ids?.length ? form.phase_ids : undefined,
        team_ids: form.team_ids?.length ? form.team_ids : undefined,
        new_phases: newPhases.length > 0 ? newPhases : undefined,
        new_teams: newTeams.length > 0 ? newTeams : undefined,
        scenario_ids: selectedScenarioIds.length ? selectedScenarioIds : undefined,
      };
      if (editingId != null) {
        await v2Api.dashboard.scenarios.injects.update(scenarioId, editingId, payload);
      } else {
        await v2Api.dashboard.scenarios.injects.create(scenarioId, payload);
      }
      clearFormParams();
      fetchInjects();
    } catch (err: unknown) {
      const data = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: unknown } }).response?.data
        : null;
      const message = formatApiError(data, err);
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeleteInjectId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (deleteInjectId == null) return;
    setIsDeleting(true);
    try {
      await v2Api.dashboard.scenarios.injects.delete(scenarioId, deleteInjectId);
      setDeleteDialogOpen(false);
      setDeleteInjectId(null);
      fetchInjects();
    } catch (err) {
      console.error('Delete inject failed:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const addOption = () => {
    setForm((f) => ({
      ...f,
      options: [...(f.options ?? []), { text: '', order: (f.options?.length ?? 0), is_correct: false }],
    }));
  };

  const updateOption = (index: number, field: 'text' | 'order' | 'is_correct', value: string | number | boolean) => {
    setForm((f) => {
      const opts = [...(f.options ?? [])];
      if (!opts[index]) return f;
      opts[index] = { ...opts[index], [field]: value };
      return { ...f, options: opts };
    });
  };

  const removeOption = (index: number) => {
    setForm((f) => ({
      ...f,
      options: (f.options ?? []).filter((_, i) => i !== index),
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-center text-sm text-destructive">
        {error}
      </div>
    );
  }

  return (
    <>
      {showForm && (
        <Card className="mb-6">
          <CardHeader className="pb-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle>{editingId != null ? 'Edit Inject' : 'Add Inject'}</CardTitle>
                <CardDescription className="mt-1">
                  Create or update a tabletop exercise inject for this scenario.
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={clearFormParams} disabled={saving}>
                  Cancel
                </Button>
                {formStep > 0 ? (
                  <Button variant="outline" size="sm" onClick={() => setFormStep((s) => s - 1)}>
                    Back
                  </Button>
                ) : null}
                {formStep < 4 && (
                  <Button size="sm" onClick={() => setFormStep((s) => s + 1)}>
                    Next
                  </Button>
                )}
                <Button
                  size="sm"
                  className="disabled:cursor-not-allowed"
                  onClick={handleSave}
                  disabled={formStep < 1 ? true : saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : editingId != null ? (
                    'Update'
                  ) : (
                    'Create'
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          <div className="px-6 pb-6 grid gap-4">
            {saveError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
                {saveError}
              </div>
            )}
            <div className="flex gap-1 border-b pb-3 mb-1">
              {[
                { i: 0, label: 'Basics' },
                { i: 1, label: 'Score' },
                { i: 2, label: 'Answer & options' },
                { i: 3, label: 'Phases & teams' },
                { i: 4, label: 'Exercises' },
              ].map(({ i, label }) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setFormStep(i)}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition ${
                    formStep === i
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {i + 1}. {label}
                </button>
              ))}
            </div>
            {formStep === 0 && (
            <>
            <div className="grid gap-2">
              <Label htmlFor="inject-title">Title</Label>
              <Input
                id="inject-title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Inject title"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="inject-description">
                {isFillBlank ? 'Sentence with blank(s)' : 'Description'}
              </Label>
              <textarea
                id="inject-description"
                className={textareaClass}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder={
                  isFillBlank
                    ? 'e.g. The _____ protocol is used for secure web traffic.'
                    : 'Description'
                }
                rows={4}
              />
              {isFillBlank && (
                <p className="text-xs text-muted-foreground">
                  Use _____ or ... to show where the blank(s) are. Participants will type the missing word(s).
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="inject-order">Order</Label>
                <Input
                  id="inject-order"
                  type="number"
                  min={1}
                  value={form.order ?? 1}
                  onChange={(e) => setForm((f) => ({ ...f, order: parseInt(e.target.value, 10) || 1 }))}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="inject-severity">Severity</Label>
                <select
                  id="inject-severity"
                  className="flex h-9 w-full rounded-md border border-input bg-background text-foreground px-3 py-1 text-sm shadow-sm"
                  value={form.severity ?? 'medium'}
                  onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
                >
                  {SEVERITY_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="inject-type">Inject type</Label>
              <select
                id="inject-type"
                className="flex h-9 w-full rounded-md border border-input bg-background text-foreground px-3 py-1 text-sm shadow-sm"
                value={form.inject_type || ''}
                onChange={(e) => {
                  const typeId = (parseInt(e.target.value, 10) || injectTypes[0]?.id) ?? 0;
                  const newType = injectTypes.find((t) => t.id === typeId);
                  setForm((f) => ({
                    ...f,
                    inject_type: typeId,
                    options:
                      newType?.code === 'true_false'
                        ? TRUE_FALSE_OPTIONS.map((o) => ({ ...o }))
                        : f.options,
                  }));
                }}
              >
                {injectTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            {/* end of basics inputs; score moved to next step */}
            </>
            )}
            {formStep === 1 && (
            <>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="inject-points">Points</Label>
                <Input
                  id="inject-points"
                  type="number"
                  min={1}
                  value={form.points ?? 1}
                  onChange={(e) => {
                    const value = e.target.value;
                    const parsed = parseInt(value, 10);
                    setForm((f) => ({
                      ...f,
                      points: Number.isNaN(parsed) ? f.points : parsed,
                    }));
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="inject-attempts">Max answer attempts</Label>
                <Input
                  id="inject-attempts"
                  type="number"
                  min={1}
                  value={form.max_answer_attempts ?? 3}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, max_answer_attempts: parseInt(e.target.value, 10) || 1 }))
                  }
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="inject-points-type">Points type</Label>
              <select
                id="inject-points-type"
                className="flex h-9 w-full rounded-md border border-input bg-background text-foreground px-3 py-1 text-sm shadow-sm"
                value={form.points_type ?? 'default'}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    points_type: (e.target.value as 'default' | 'manual' | 'decay') || 'default',
                  }))
                }
              >
                {POINTS_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="inject-offset">Scheduled offset (minutes)</Label>
              <Input
                id="inject-offset"
                type="number"
                min={0}
                value={form.scheduled_offset_minutes ?? 0}
                onChange={(e) =>
                  setForm((f) => ({ ...f, scheduled_offset_minutes: parseInt(e.target.value, 10) || 0 }))
                }
              />
            </div>
            </>
            )}
            {formStep === 2 && (
            <>
            {hasOptions && (
              <div className="grid gap-2">
                <div className="flex items-center justify-between">
                  <Label>
                    {isTrueFalse
                      ? 'Mark the correct answer'
                      : isMultipleSelect
                        ? 'Options (mark all correct answers)'
                        : 'Options (mark correct)'}
                  </Label>
                  {!isTrueFalse && (
                    <Button type="button" variant="outline" size="sm" onClick={addOption}>
                      Add option
                    </Button>
                  )}
                </div>
                {isMultipleSelect && (
                  <p className="text-xs text-muted-foreground">
                    Participants can select more than one option. Check every option that is correct.
                  </p>
                )}
                {(form.options ?? []).map((opt, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      placeholder="Option text"
                      value={opt.text}
                      onChange={(e) => updateOption(idx, 'text', e.target.value)}
                      className="flex-1"
                      readOnly={isTrueFalse}
                    />
                    <label className="flex items-center gap-1 shrink-0 text-sm">
                      <input
                        type="checkbox"
                        checked={opt.is_correct}
                        onChange={(e) => updateOption(idx, 'is_correct', e.target.checked)}
                      />
                      Correct
                    </label>
                    {!isTrueFalse && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeOption(idx)} aria-label="Remove">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {!hasOptions && (
              <div className="grid gap-2">
                <Label htmlFor="inject-correct">
                  {isShortAnswer
                    ? 'Expected short answer (for auto-scoring)'
                    : isFillBlank
                      ? 'Correct word(s) for the blank(s)'
                      : 'Correct / expected answer (optional)'}
                </Label>
                <Input
                  id="inject-correct"
                  value={form.correct_answer ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, correct_answer: e.target.value }))}
                  placeholder={
                    isShortAnswer
                      ? 'Exact or key phrase to match participant\'s answer'
                      : isFillBlank
                        ? 'One word per blank, e.g. HTTPS or word1|word2 for multiple'
                        : 'Expected answer text'
                  }
                />
              </div>
            )}
            {(() => {
              const selectedType = injectTypes.find((t) => t.id === form.inject_type);
              const detailMediaType = selectedType?.detail_alert_media_type ?? '';
              if (!detailMediaType) return null;
              const showImage = detailMediaType === 'image_only' || detailMediaType === 'mixed';
              const showText = detailMediaType === 'text' || detailMediaType === 'mixed';
              return (
                <div className="grid gap-2 border-t pt-4 mt-2">
                  <Label>Detail / Alert (optional)</Label>
                  <p className="text-xs text-muted-foreground">
                    Based on inject type &quot;{selectedType?.name}&quot;: {detailMediaType === 'mixed' ? 'image and/or text' : detailMediaType === 'image_only' ? 'image' : 'text'}.
                  </p>
                  {showImage && (
                    <div className="grid gap-2">
                      <Label htmlFor="detail-alert-image" className="text-sm font-normal">Image URL</Label>
                      <Input
                        id="detail-alert-image"
                        type="url"
                        value={form.detail_alert_image_url ?? ''}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, detail_alert_image_url: e.target.value }))
                        }
                        placeholder="https://..."
                      />
                    </div>
                  )}
                  {showText && (
                    <div className="grid gap-2">
                      <Label htmlFor="detail-alert-text" className="text-sm font-normal">Text content</Label>
                      <textarea
                        id="detail-alert-text"
                        className={textareaClass}
                        value={form.detail_alert_text ?? ''}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, detail_alert_text: e.target.value }))
                        }
                        placeholder="Additional text or HTML"
                        rows={3}
                      />
                    </div>
                  )}
                </div>
              );
            })()}
            </>
            )}
            {formStep === 3 && (
            <>
            <div className="grid gap-2">
              <Label>Phases (optional)</Label>
              <p className="text-xs text-muted-foreground">
                Select existing phases below, or type a name to add. Duplicates are not allowed.
              </p>
              {/* One place: all selected phases (existing + new) */}
              <div className="flex items-start gap-2 border rounded-md px-3 py-2 bg-muted/40">
                <input
                  id="inject-apply-all-phases"
                  type="checkbox"
                  className="mt-1"
                  checked={form.apply_to_all_phases ?? false}
                  onChange={(e) => setForm((f) => ({ ...f, apply_to_all_phases: e.target.checked }))}
                />
                <div className="space-y-0.5">
                  <Label htmlFor="inject-apply-all-phases">Apply to all phases</Label>
                  <p className="text-xs text-muted-foreground">
                    When enabled, this inject will apply to all phases, even if no specific phases are selected.
                  </p>
                </div>
              </div>
              {!form.apply_to_all_phases && ((form.phase_ids ?? []).length > 0 || newPhases.length > 0) && (
                <div className="rounded-md border bg-muted/30 p-3">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Selected phases</p>
                  <div className="flex flex-wrap gap-2">
                    {(form.phase_ids ?? []).map((pid) => {
                      const p = phases.find((x) => x.id === pid);
                      if (!p) return null;
                      return (
                        <span
                          key={p.id}
                          className="inline-flex items-center gap-1 rounded bg-primary/15 px-2 py-1 text-sm"
                        >
                          {p.name}
                          <button
                            type="button"
                            onClick={() =>
                              setForm((f) => ({
                                ...f,
                                phase_ids: (f.phase_ids ?? []).filter((id) => id !== p.id),
                              }))
                            }
                            className="rounded p-0.5 hover:bg-primary/30"
                            aria-label="Remove"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                    {newPhases.map((np, idx) => (
                      <span
                        key={`new-${idx}`}
                        className="inline-flex items-center gap-1 rounded bg-primary/15 px-2 py-1 text-sm"
                      >
                        {np.name}
                        <button
                          type="button"
                          onClick={() => setNewPhases((prev) => prev.filter((_, i) => i !== idx))}
                          className="rounded p-0.5 hover:bg-primary/30"
                          aria-label="Remove"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {!form.apply_to_all_phases && phases.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {phases.map((p) => {
                    const selected = (form.phase_ids ?? []).includes(p.id);
                    return (
                      <label
                        key={p.id}
                        className="flex items-center gap-2 rounded border px-3 py-2 cursor-pointer hover:bg-muted/50"
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            setForm((f) => {
                              const ids = f.phase_ids ?? [];
                              if (e.target.checked) {
                                if (ids.includes(p.id)) return f;
                                return { ...f, phase_ids: [...ids, p.id] };
                              }
                              return { ...f, phase_ids: ids.filter((id) => id !== p.id) };
                            });
                          }}
                        />
                        <span className="text-sm">{p.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {!form.apply_to_all_phases && (
              <div className="flex flex-wrap items-end gap-2">
                <Input
                  placeholder="New phase name"
                  value={newPhaseName}
                  onChange={(e) => {
                    setNewPhaseName(e.target.value);
                    setPhaseError(null);
                  }}
                  className="max-w-[180px]"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const name = newPhaseName.trim();
                    setPhaseError(null);
                    if (!name) return;
                    const nameLower = name.toLowerCase();
                    const slug = nameLower.replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                    const alreadyInNew = newPhases.some((np) => np.name.toLowerCase() === nameLower);
                    if (alreadyInNew) {
                      setPhaseError('Duplicate phase not allowed.');
                      return;
                    }
                    const existing = phases.find(
                      (p) =>
                        p.name.toLowerCase() === nameLower ||
                        p.slug?.toLowerCase() === slug
                    );
                    if (existing) {
                      const alreadySelected = (form.phase_ids ?? []).includes(existing.id);
                      if (alreadySelected) {
                        setPhaseError('Duplicate phase not allowed.');
                        return;
                      }
                      setForm((f) => ({
                        ...f,
                        phase_ids: [...(f.phase_ids ?? []), existing.id],
                      }));
                      setNewPhaseName('');
                      return;
                    }
                    setNewPhases((prev) => [...prev, { name, order: phases.length + prev.length }]);
                    setNewPhaseName('');
                  }}
                >
                  <Plus className="mr-1 h-3 w-3" /> Add phase
                </Button>
              </div>
              )}
              {phaseError && (
                <p className="text-sm text-destructive">{phaseError}</p>
              )}
            </div>
            <div className="grid gap-2">
              <Label>Teams (optional)</Label>
              <p className="text-xs text-muted-foreground">
                Select existing teams below, or type a name to add. Duplicates are not allowed.
              </p>
              <div className="flex items-start gap-2 border rounded-md px-3 py-2 bg-muted/40">
                <input
                  id="inject-apply-all-teams"
                  type="checkbox"
                  className="mt-1"
                  checked={form.apply_to_all_team ?? false}
                  onChange={(e) => setForm((f) => ({ ...f, apply_to_all_team: e.target.checked }))}
                />
                <div className="space-y-0.5">
                  <Label htmlFor="inject-apply-all-teams">Apply to all teams</Label>
                  <p className="text-xs text-muted-foreground">
                    When enabled, this inject will apply to all participant teams, even if no specific teams are selected.
                  </p>
                </div>
              </div>
              {/* One place: all selected roles (existing + new) */}
              {!form.apply_to_all_team && ((form.team_ids ?? []).length > 0 || newTeams.length > 0) && (
                <div className="rounded-md border bg-muted/30 p-3">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Selected teams</p>
                  <div className="flex flex-wrap gap-2">
                    {(form.team_ids ?? []).map((rid) => {
                      const r = teams.find((x) => x.id === rid);
                      if (!r) return null;
                      return (
                        <span
                          key={r.id}
                          className="inline-flex items-center gap-1 rounded bg-primary/15 px-2 py-1 text-sm"
                        >
                          {r.name}
                          <button
                            type="button"
                            onClick={() =>
                              setForm((f) => ({
                                ...f,
                                team_ids: (f.team_ids ?? []).filter((id) => id !== r.id),
                              }))
                            }
                            className="rounded p-0.5 hover:bg-primary/30"
                            aria-label="Remove"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                    {newTeams.map((nr, idx) => (
                      <span
                        key={`new-${idx}`}
                        className="inline-flex items-center gap-1 rounded bg-primary/15 px-2 py-1 text-sm"
                      >
                        {nr.name}
                        <button
                          type="button"
                          onClick={() => setNewTeams((prev) => prev.filter((_, i) => i !== idx))}
                          className="rounded p-0.5 hover:bg-primary/30"
                          aria-label="Remove"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {!form.apply_to_all_team && teams.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {teams.map((r) => {
                    const selected = (form.team_ids ?? []).includes(r.id);
                    return (
                      <label
                        key={r.id}
                        className="flex items-center gap-2 rounded border px-3 py-2 cursor-pointer hover:bg-muted/50"
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            setForm((f) => {
                              const ids = f.team_ids ?? [];
                              if (e.target.checked) {
                                if (ids.includes(r.id)) return f;
                                return { ...f, team_ids: [...ids, r.id] };
                              }
                              return { ...f, team_ids: ids.filter((id) => id !== r.id) };
                            });
                          }}
                        />
                        <span className="text-sm">{r.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {!form.apply_to_all_team && (
              <div className="flex flex-wrap items-end gap-2">
                <Input
                  placeholder="New team name"
                  value={newTeamName}
                  onChange={(e) => {
                    setNewTeamName(e.target.value);
                    setTeamError(null);
                  }}
                  className="max-w-[180px]"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const name = newTeamName.trim();
                    setTeamError(null);
                    if (!name) return;
                    const nameLower = name.toLowerCase();
                    const code = nameLower.replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                    const alreadyInNew = newTeams.some((nr) => nr.name.toLowerCase() === nameLower);
                    if (alreadyInNew) {
                      setTeamError('Duplicate team not allowed.');
                      return;
                    }
                    const existing = teams.find(
                      (r) =>
                        r.name.toLowerCase() === nameLower ||
                        r.code?.toLowerCase() === code
                    );
                    if (existing) {
                      const alreadySelected = (form.team_ids ?? []).includes(existing.id);
                      if (alreadySelected) {
                        setTeamError('Duplicate team not allowed.');
                        return;
                      }
                      setForm((f) => ({
                        ...f,
                        team_ids: [...(f.team_ids ?? []), existing.id],
                      }));
                      setNewTeamName('');
                      return;
                    }
                    setNewTeams((prev) => [...prev, { name }]);
                    setNewTeamName('');
                  }}
                >
                  <Plus className="mr-1 h-3 w-3" /> Add team
                </Button>
              </div>
              )}
              {teamError && (
                <p className="text-sm text-destructive">{teamError}</p>
              )}
            </div>
            </>
            )}
            {formStep === 4 && (
            <>
            <div className="grid gap-2">
              <Label>Exercises</Label>
              <p className="text-xs text-muted-foreground">
                Map this inject to one or more tabletop exercise scenarios. Only TTX-enabled
                scenarios are shown.
              </p>
              <div className="grid gap-2 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
                <div className="grid gap-2">
                  <Label htmlFor="scenario-search" className="text-xs text-muted-foreground">
                    Search scenarios by name
                  </Label>
                  <Input
                    id="scenario-search"
                    value={scenarioSearch}
                    onChange={(e) => setScenarioSearch(e.target.value)}
                    placeholder="Start typing to filter scenarios..."
                  />
                  {scenariosLoading ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span>Loading scenarios…</span>
                    </div>
                  ) : scenariosError ? (
                    <p className="text-xs text-destructive mt-1">{scenariosError}</p>
                  ) : allScenarios.length === 0 ? (
                    <p className="text-xs text-muted-foreground mt-1">
                      No TTX-enabled scenarios are available.
                    </p>
                  ) : (
                    <div className="mt-2 max-h-48 overflow-y-auto rounded-md border bg-muted/30 p-2 space-y-1">
                      {allScenarios
                        .filter((s) => {
                          const q = scenarioSearch.trim().toLowerCase();
                          if (!q) return true;
                          return s.name.toLowerCase().includes(q) || s.id.toLowerCase().includes(q);
                        })
                        .map((s) => {
                          const selected = selectedScenarioIds.includes(s.id);
                          return (
                            <label
                              key={s.id}
                              className="flex items-center gap-2 rounded px-2 py-1 cursor-pointer hover:bg-muted"
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={(e) => {
                                  setSelectedScenarioIds((prev) => {
                                    if (e.target.checked) {
                                      if (prev.includes(s.id)) return prev;
                                      return [...prev, s.id];
                                    }
                                    return prev.filter((id) => id !== s.id);
                                  });
                                }}
                              />
                              <span className="text-sm flex-1 min-w-0">
                                <span className="font-medium truncate">{s.name}</span>
                                <span className="ml-2 text-[11px] font-mono text-muted-foreground">
                                  {s.id}
                                </span>
                              </span>
                            </label>
                          );
                        })}
                    </div>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label className="text-xs text-muted-foreground">Selected scenarios</Label>
                  {selectedScenarioIds.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      This inject will only be available in the current scenario unless you select
                      additional ones.
                    </p>
                  ) : (
                    <div className="rounded-md border bg-muted/20 p-2 flex flex-wrap gap-1">
                      {selectedScenarioIds.map((sid) => {
                        const s = allScenarios.find((x) => x.id === sid);
                        return (
                          <span
                            key={sid}
                            className="inline-flex items-center gap-1 rounded bg-primary/15 px-2 py-0.5 text-xs"
                          >
                            {s?.name ?? 'Scenario'}{' '}
                            <span className="font-mono text-[11px] text-muted-foreground">
                              {sid}
                            </span>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-[11px] text-muted-foreground">
                    Changes here are saved when you click <span className="font-semibold">Update</span> or{' '}
                    <span className="font-semibold">Create</span>.
                  </p>
                </div>
              </div>
            </div>
            </>
            )}
          </div>
        </Card>
      )}

      {!showForm && (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              {injects.length} inject{injects.length !== 1 ? 's' : ''}
            </p>
            <Button onClick={openCreate} size="sm">
              <Plus className="mr-2 h-4 w-4" />
              Add Inject
            </Button>
          </div>
          <div className="space-y-3">
            {injects.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No injects yet. Add one to get started.</p>
            ) : (
              injects
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((inject) => (
                  <Card key={inject.id}>
                    <CardHeader className="py-3 px-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-base flex items-center gap-2">
                            <span className="text-muted-foreground font-normal">#{inject.order}</span>
                            {inject.title}
                          </CardTitle>
                          <CardDescription className="mt-1 line-clamp-2">{inject.description}</CardDescription>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <span className="text-xs px-2 py-0.5 rounded bg-muted capitalize">
                              {inject.severity}
                            </span>
                            {inject.inject_type?.name && (
                              <span className="text-xs px-2 py-0.5 rounded bg-muted">
                                {inject.inject_type.name}
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {inject.points ?? 0} pts
                              {inject.points_type && (
                                <>
                                  {' '}
                                  ·{' '}
                                  {inject.points_type === 'manual'
                                    ? 'Manual'
                                    : inject.points_type === 'decay'
                                    ? 'Decay'
                                    : 'Default'}
                                </>
                              )}
                            </span>
                            {inject.scheduled_offset_minutes > 0 && (
                              <span className="text-xs text-muted-foreground">
                                +{inject.scheduled_offset_minutes} min
                              </span>
                            )}
                            {Array.isArray(inject.ttx_config_ids) &&
                              inject.ttx_config_ids.length > 1 && (
                                <span className="text-xs text-muted-foreground">
                                  Linked to {inject.ttx_config_ids.length} scenarios
                                </span>
                              )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(inject)} aria-label="Edit">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(inject.id)}
                            aria-label="Delete"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                ))
            )}
          </div>

          <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
            if (!open) {
              setDeleteDialogOpen(false);
              setDeleteInjectId(null);
            }
          }}>
            <DialogContent className="sm:max-w-[420px]">
              <DialogHeader>
                <DialogTitle>Delete this inject?</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete this inject? This cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={confirmDelete} disabled={isDeleting}>
                  {isDeleting ? 'Deleting...' : 'Delete Inject'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </>
  );
}
